import { BaseCreateItemAttrs } from "../../features/catalogs/catalog.design";
import { CatalogPageRef } from "../../features/catalogs/catalogs";
import { DesignItem } from "../../features/design/design.types";
import {
  TargetElement,
  getRotatedBounds,
  roundCoord,
  roundPos,
  templateGridSize,
} from "../../features/design/designer";
import { processImage } from "../../features/catalogs/catalog.util";
import { getDimensions } from "../../features/catalogs/catalog.page";
import { CatalogBuilder } from "../../features/catalogs/catalog.builder";

export const processDesignImages = () => {
  const images = Array.from(document.getElementsByClassName("content-image"));
  setTimeout(() => {
    images.forEach((img) => {
      processImage(img as HTMLImageElement, ".board-item");
    });
  }, 100);
};

export const translateElementCoordinates = (
  element: TargetElement,
  builder: CatalogBuilder,
  rotation?: number
) => {
  const d = getDimensions(builder);

  //zoom = zoom || 1;
  const b = element.getBoundingClientRect();
  const he = element as HTMLElement;
  if (he.offsetWidth) {
    b.x = he.offsetLeft;
    b.y = he.offsetTop;
    b.width = he.offsetWidth;
    b.height = he.offsetHeight;
  }
  let l = Math.floor(b.x),
    t = Math.floor(b.y);
  const w = b.width,
    h = b.height;
  // `data-rotate` only exists mid-gesture, so the caller passes the item's
  // stored rotation for elements that were loaded already rotated.
  const r = rotation ?? +(element.dataset.rotate || 0);
  // offsetLeft/Width describe the UNROTATED layout box, but what the user drags
  // (and what react-moveable bounds) is the rotated box. Clamp that one, so the
  // limits are symmetric — the old code floored left/top at 0 while capping
  // right/bottom against the layout box, which pinned rotated elements on the
  // left and let them drift past the page on the right.
  const rb = getRotatedBounds(w, h, r);
  const offX = (w - rb.w) / 2;
  const offY = (h - rb.h) / 2;
  const dw = d.w - w + offX;
  const dh = d.h - h + offY;
  if (l > dw) {
    l = dw;
  }
  if (t > dh) {
    t = dh;
  }
  l = Math.max(l, -offX);
  t = Math.max(t, -offY);
  return {
    x: roundPos(l, d.scaleX),
    y: roundPos(t, d.scaleY),
    w: roundCoord(w, d.scaleX),
    h: roundCoord(h, d.scaleY),
    left: l,
    top: t,
    width: w,
    height: h,
    scaleX: d.scaleX,
    scaleY: d.scaleY,
    r,
  };
};

export type ElementDragProps = BaseCreateItemAttrs & {
  // type: DesignItemType;
  // subtype?: DesignSubtype;
  // contentType?: DesignItemType;
  // content?: string;
  // productId?: string;
  // templateId?: string;
  dragImgSrc?: string;
  clipboardIndex?: string;
};

export let onBoardDropGlobalHandle: () => void;

export const startElementDrag = (
  e: React.DragEvent<HTMLDivElement>,
  props: ElementDragProps,
  onDrop?: () => void
) => {
  onBoardDropGlobalHandle = onDrop;
  const designerContainer = ".designer-container";
  e.dataTransfer.setData("type", props.type);
  e.dataTransfer.setData("contentType", props.contentType ?? props.type);
  e.dataTransfer.setData("content", props.content);
  if (props.subtype) e.dataTransfer.setData("subtype", props.subtype);
  if (props.productId) e.dataTransfer.setData("productId", props.productId);
  if (props.templateId) e.dataTransfer.setData("templateId", props.templateId);
  if (props.clipboardIndex)
    e.dataTransfer.setData("clipboardIndex", props.clipboardIndex);
  if (props.dragImgSrc) {
    const img = new Image();
    img.src = props.dragImgSrc;
    e.dataTransfer.setDragImage(img, 5, 5);
  }
  e.dataTransfer.effectAllowed = "copy";
  const c = e.currentTarget.closest(designerContainer);
  c?.classList.add("dragging");
  //document.querySelector(designerContainer)?.classList.add("dragging");
  const selectors = [];
  if (props.type === "image" || props.type === "product-image") {
    selectors.push("image");
  }
  if (props.type === "product") {
    selectors.push("image", "text", "template");
  }

  if (selectors.length) {
    const query = selectors.reduce(
      (a, s) => a + (a ? ", " : "") + designerContainer + " .board-item-" + s,
      ""
    );
    const elements = document.querySelectorAll(query);
    elements.forEach((e) => {
      if (!e.classList.contains("extension")) {
        e.classList.add("element-drop-target");
      }
    });
  }
};

export const endElementDrag = () => {
  onBoardDropGlobalHandle = undefined;
  const designerContainer = ".designer-container";
  Array.from(document.querySelectorAll(designerContainer)).forEach((e) => {
    e.classList.remove("dragging");
  });
  Array.from(
    document.querySelectorAll(
      designerContainer + " .board-item.element-drop-target"
    )
  ).forEach((e) => {
    e.classList.remove("element-drop-target");
  });
  // document
  //   .querySelector(designerContainer + " .board-item.element-drop-target")
  //   ?.classList.remove("element-drop-target");
};

export const processItemImages = (el: string | HTMLElement) => {
  const target = typeof el === "string" ? document.querySelector(el) : el;
  if (!target) return;
  const images = Array.from(target.getElementsByClassName("content-image"));
  if (images.length > 0) {
    images.forEach((img) => {
      processImage(img as HTMLImageElement);
    });
  }
};

export const positionElement = (di: DesignItem, page: CatalogPageRef) => {
  if (!page.addedElements) page.addedElements = 0;

  const pad = 20; // margin from edges
  const gap = 30; // spacing between elements
  const elW = di.w || 200;
  const elH = di.h || 200;
  const usable = templateGridSize - pad * 2;
  const cols = Math.max(1, Math.floor((usable + gap) / (elW + gap)));
  const idx = page.addedElements;
  const col = idx % cols;
  const row = Math.floor(idx / cols);

  di.x = pad + col * (elW + gap);
  di.y = pad + row * (elH + gap);

  // Reset counter if element would overflow the grid
  page.addedElements =
    di.x + di.w > templateGridSize || di.y + di.h > templateGridSize
      ? 0
      : page.addedElements + 1;
};
